import { useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  AlertTriangle,
  Building2,
  ChevronDown,
  Layers,
  Loader2,
  Repeat,
  Server,
  ShieldCheck,
  X,
} from "lucide-react";
import { DataTable, type Column } from "../../components/DataTable";
import type {
  AssetTypeFailureRow,
  OrgApplicationRow,
  OrgControlGroupRow,
  OrgDrillDownData,
  OrgFailingControlRow,
} from "./orgScope";

export type DrillView = "families" | "repeat" | "applications" | "assets";

const DRILL_PANEL_ID = "org-drill-panel";

const DRILL_VIEW_TITLE: Record<DrillView, string> = {
  families: "Failing Control Families",
  repeat: "Repeatedly Failing Controls",
  applications: "Applications",
  assets: "Failed Component Type",
};

const CARD_CLASS =
  "rounded-lg border border-[#e2e8f0] dark:border-white/[0.06] overflow-hidden bg-white dark:bg-gradient-to-br dark:from-[#171728] dark:to-[#0e0e1a] shadow-sm dark:shadow-[0_1px_2px_rgba(0,0,0,0.3)]";

const EMPTY_CELL = "-";
const SEV_CRIT = "#ef4444";
const SEV_HIGH = "#f97316";
const SEV_MED = "#f59e0b";
const SEV_LOW = "#10b981";

function cycleColor(cycles: number): string {
  if (cycles >= 4) return SEV_CRIT;
  if (cycles === 3) return SEV_HIGH;
  if (cycles === 2) return SEV_MED;
  return SEV_LOW;
}
function sourceLabel(source: string): string {
  if (source === "BOTH") return "Manual + Automated";
  if (source === "MANUAL") return "Manual";
  if (source === "AUTOMATED") return "Automated";
  return source;
}
export function OrgDetailsDrawer({
  data,
  open,
  loading,
  onClose,
  onSelectApplication,
}: {
  data: OrgDrillDownData | null;
  open: boolean;
  loading: boolean;
  onClose: () => void;
  onSelectApplication?: (application: OrgApplicationRow) => void;
}) {
  const [view, setView] = useState<DrillView>("families");
  if (!open) return null;
  const controls = data?.controls ?? [];
  const families = data?.controlGroups ?? [];
  const applications = data?.applications ?? [];
  const assetTypes = data?.byAssetType ?? [];

  return createPortal(
    <div className="fixed inset-0 z-50 flex">
      <div
        className="absolute inset-0 bg-black/55 dark:bg-black/75 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div className="relative ml-auto w-full max-w-3xl bg-white dark:bg-gradient-to-br dark:from-[#131320] dark:to-[#0a0a14] shadow-2xl overflow-hidden flex flex-col">
        {loading || !data ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="size-5 animate-spin text-indigo-500 mr-2" />
            <span className="text-sm text-[#64748b] dark:text-white/55">
              Loading organization details
            </span>
          </div>
        ) : (
          <>
            <OrgDrillHeader data={data} onClose={onClose} />
            <div className="flex-1 overflow-y-auto">
              <div className="px-5 pt-4">
                <OrgMetricTiles data={data} view={view} onSelect={setView} />
              </div>
              <div
                id={DRILL_PANEL_ID}
                role="tabpanel"
                className="border-t border-[#e2e8f0] dark:border-white/[0.06] mt-4"
              >
                <Section title={DRILL_VIEW_TITLE[view]}>
                  {view === "families" && (
                    <FamilyAccordion controls={controls} families={families} />
                  )}
                  {view === "repeat" && <RepeatFailuresList controls={controls} />}
                  {view === "applications" && (
                    <ApplicationsList
                      applications={applications}
                      onSelect={onSelectApplication}
                    />
                  )}
                  {view === "assets" && <AssetTypesList rows={assetTypes} />}
                </Section>
              </div>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
function OrgDrillHeader({
  data,
  onClose,
}: {
  data: OrgDrillDownData;
  onClose: () => void;
}) {
  return (
    <div className="px-5 py-4 border-b border-[#e2e8f0] dark:border-white/[0.06] bg-[#f8fafc] dark:bg-white/[0.02]">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <span className="inline-flex items-center justify-center size-9 rounded-lg bg-indigo-600/10 dark:bg-indigo-400/15 flex-shrink-0">
            <Building2 className="size-4.5 text-indigo-600 dark:text-indigo-300" />
          </span>
          <div className="min-w-0">
            <p className="text-lg font-bold text-[#0f172a] dark:text-white leading-tight">
              {data.orgName}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close organization details"
          className="flex-shrink-0 inline-flex items-center justify-center size-8 rounded-lg text-[#64748b] dark:text-white/55 hover:bg-[#e2e8f0] dark:hover:bg-white/[0.08] transition-colors"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}
function OrgMetricTiles({
  data,
  view,
  onSelect,
}: {
  data: OrgDrillDownData;
  view: DrillView;
  onSelect: (view: DrillView) => void;
}) {
  const tiles: {
    id: DrillView;
    label: string;
    value: number;
    total?: number;
    icon: typeof AlertTriangle;
    accent: string;
  }[] = [
    {
      id: "families",
      label: "Failed Controls",
      value: data.failedControlCount,
      icon: AlertTriangle,
      accent: SEV_CRIT,
    },
    {
      id: "repeat",
      label: "Repeat Failures",
      value: data.continuousFailedControlCount,
      icon: Repeat,
      accent: SEV_HIGH,
    },
    {
      id: "applications",
      label: "Applications",
      value: data.applicationCount,
      icon: Layers,
      accent: "#4f46e5",
    },
    {
      id: "assets",
      label: "Failed Assets",
      value: data.assetsFailing ?? 0,
      total: data.assetCount ?? 0,
      icon: Server,
      accent: "#0ea5e9",
    },
  ];
  return (
    <div
      className="grid grid-cols-2 md:grid-cols-4 gap-3"
      role="tablist"
      aria-label="Organization breakdown"
    >
      {tiles.map((t) => {
        const active = t.id === view;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={DRILL_PANEL_ID}
            onClick={() => onSelect(t.id)}
            className={`text-left rounded-xl border px-3 py-2.5 transition-colors cursor-pointer ${
              active
                ? "bg-white dark:bg-white/[0.06]"
                : "border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-white/[0.02] hover:border-[#cbd5e1] dark:hover:border-white/[0.14]"
            }`}
            style={
              active
                ? { borderColor: t.accent, boxShadow: `inset 0 0 0 1px ${t.accent}` }
                : undefined
            }
          >
            <div className="flex items-center justify-between gap-2">
              <span
                className="text-[11px] font-semibold"
                style={{
                  color: active ? t.accent : undefined,
                }}
              >
                <span className={active ? "" : "text-[#64748b] dark:text-white/55"}>
                  {t.label}
                </span>
              </span>
              <t.icon className="size-3.5" style={{ color: t.accent }} />
            </div>
            <p className="mt-1 whitespace-nowrap text-xl font-bold tabular-nums text-[#0f172a] dark:text-white">
              {t.value}
              {t.total !== undefined && (
                <span className="ml-1 text-[13px] font-semibold text-[#64748b] dark:text-white/55">
                  / {t.total}
                </span>
              )}
            </p>
          </button>
        );
      })}
    </div>
  );
}
function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="px-5 py-4 flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[14px] font-bold tracking-tight text-[#0f172a] dark:text-white">
          {title}
        </p>
        {action}
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
}
function FamilyAccordion({
  controls,
  families,
}: {
  controls: OrgFailingControlRow[];
  families: OrgControlGroupRow[];
}) {
  const groups = useMemo(() => groupByFamily(controls, families), [controls, families]);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  if (groups.length === 0) {
    return <EmptyPanel label="No failing controls in this organization" />;
  }
  const toggle = (family: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(family)) {
        next.delete(family);
      } else {
        next.add(family);
      }
      return next;
    });
  return (
    <div className="space-y-2">
      {groups.map((g) => {
        const isOpen = !collapsed.has(g.family);
        const panelId = `family-panel-${g.family}`;
        return (
          <div key={g.family} className={CARD_CLASS}>
            <button
              type="button"
              onClick={() => toggle(g.family)}
              aria-expanded={isOpen}
              aria-controls={panelId}
              className="w-full flex items-center gap-3 px-3 h-11 text-left bg-[#f8fafc] dark:bg-white/[0.02] hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors cursor-pointer"
            >
              <ChevronDown
                className={`size-4 flex-shrink-0 text-[#64748b] dark:text-white/55 transition-transform ${
                  isOpen ? "" : "-rotate-90"
                }`}
              />
              <span className="inline-flex items-center justify-center h-5 px-2 rounded-md text-[10px] font-bold tracking-[0.08em] tabular-nums bg-[#0f172a]/[0.04] dark:bg-white/[0.06] text-[#334155] dark:text-white/75 border border-[#0f172a]/[0.06] dark:border-white/[0.06] flex-shrink-0">
                {g.family.toUpperCase()}
              </span>
              <span className="flex-1 min-w-0 truncate text-[13px] font-semibold text-[#0f172a] dark:text-white">
                {g.groupName}
              </span>
              <span className="flex-shrink-0 text-[11px] tabular-nums text-[#64748b] dark:text-white/55">
                {g.controls.length} control{g.controls.length === 1 ? "" : "s"}
              </span>
            </button>
            {isOpen && (
              <div id={panelId}>
                <ControlRows controls={g.controls} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
function RepeatFailuresList({ controls }: { controls: OrgFailingControlRow[] }) {
  const repeating = useMemo(
    () =>
      controls
        .filter((c) => c.cyclesFailed >= 2)
        .sort((a, b) => b.cyclesFailed - a.cyclesFailed),
    [controls],
  );
  if (repeating.length === 0) {
    return (
      <EmptyPanel label="No control has failed more than one consecutive cycle" />
    );
  }
  return (
    <div className={CARD_CLASS}>
      <ControlRows controls={repeating} />
    </div>
  );
}
const VISIBLE_CONTROL_ROWS = 10;
const CONTROL_ROW_PX = 56;
const CONTROL_HEADER_PX = 44;
function ControlRows({ controls }: { controls: OrgFailingControlRow[] }) {
  const scrolls = controls.length > VISIBLE_CONTROL_ROWS;
  const maxHeight = scrolls
    ? `${CONTROL_HEADER_PX + VISIBLE_CONTROL_ROWS * CONTROL_ROW_PX}px`
    : undefined;
  const columns: Column<OrgFailingControlRow>[] = [
    {
      key: "controlId",
      header: "Control",
      headerAlign: "center",
      align: "center",
      width: "112px",
      minWidth: 112,
      tooltip: (c) => `${c.state} · ${c.controlId.toUpperCase()}`,
      render: (c) => {
        const color = cycleColor(c.cyclesFailed);
        return (
          <span
            className="inline-flex min-w-[68px] max-w-full items-center justify-center h-5 px-2 rounded-md text-[10px] font-bold font-mono tracking-[0.06em] whitespace-nowrap"
            style={{
              background: `${color}1F`,
              color,
              border: `1px solid ${color}33`,
            }}
          >
            {c.controlId.toUpperCase()}
          </span>
        );
      },
    },
    {
      key: "controlTitle",
      header: "Title",
      headerAlign: "center",
      align: "left",
      minWidth: 150,
      tooltip: (c) => c.controlTitle?.trim() || c.controlId.toUpperCase(),
      render: (c) => {
        const title = c.controlTitle?.trim();
        return (
          <span className="block w-full min-w-0">
            <span className="block truncate text-[12px] font-medium text-[#0f172a] dark:text-white">
              {title || (
                <span className="text-[#94a3b8] dark:text-white/40 italic">
                  Untitled control
                </span>
              )}
            </span>
            <span className="mt-0.5 block truncate text-[10px] text-[#94a3b8] dark:text-white/45">
              {controlMeta(c)}
            </span>
          </span>
        );
      },
    },
    {
      key: "cyclesFailed",
      header: "Scans",
      headerAlign: "center",
      align: "center",
      width: "84px",
      minWidth: 84,
      tooltip: (c) =>
        `Failed ${c.cyclesFailed} consecutive scan${c.cyclesFailed === 1 ? "" : "s"}`,
      render: (c) => (
        <span
          className="text-[13px] font-bold tabular-nums"
          style={{ color: cycleColor(c.cyclesFailed) }}
        >
          {c.cyclesFailed}
        </span>
      ),
    },
  ];
  return (
    <DataTable
      columns={columns}
      data={controls}
      rowKey={(c) => c.controlId}
      compact
      frameless
      emptyText="No failing controls"
      maxHeight={maxHeight}
    />
  );
}
function ApplicationsList({
  applications,
  onSelect,
}: {
  applications: OrgApplicationRow[];
  onSelect?: (application: OrgApplicationRow) => void;
}) {
  if (applications.length === 0) {
    return (
      <EmptyPanel label="No application in this organization has a published SSP" />
    );
  }
  const columns: Column<OrgApplicationRow>[] = [
    {
      key: "applicationName",
      header: "Application",
      headerAlign: "center",
      align: "left",
      minWidth: 180,
      tooltip: (a) => a.applicationName,
      render: (a) => (
        <span className="block truncate text-[12px] font-medium text-[#0f172a] dark:text-white">
          {a.applicationName}
        </span>
      ),
    },
    {
      key: "failedControlCount",
      header: "Failed Controls",
      headerAlign: "center",
      align: "center",
      width: "128px",
      minWidth: 128,
      tooltip: false,
      render: (a) =>
        a.failedControlCount > 0 ? (
          <span
            className="text-[13px] font-bold tabular-nums"
            style={{ color: SEV_CRIT }}
          >
            {a.failedControlCount}
          </span>
        ) : (
          <span className="text-[13px] font-bold tabular-nums text-[#cbd5e1] dark:text-white/25">
            {EMPTY_CELL}
          </span>
        ),
    },
    {
      key: "continuousFailedControlCount",
      header: "Repeat",
      headerAlign: "center",
      align: "center",
      width: "96px",
      minWidth: 96,
      tooltip: (a) =>
        `${a.continuousFailedControlCount} failing for 2+ consecutive cycles`,
      render: (a) =>
        a.continuousFailedControlCount > 0 ? (
          <span
            className="text-[13px] font-bold tabular-nums"
            style={{ color: SEV_HIGH }}
          >
            {a.continuousFailedControlCount}
          </span>
        ) : (
          <span className="text-[13px] font-bold tabular-nums text-[#cbd5e1] dark:text-white/25">
            {EMPTY_CELL}
          </span>
        ),
    },
  ];
  return (
    <div className={CARD_CLASS}>
      <DataTable
        columns={columns}
        data={applications}
        rowKey={(a) => a.applicationId}
        compact
        emptyText="No applications"
        onRowClick={onSelect ? (a) => onSelect(a) : undefined}
      />
    </div>
  );
}
interface FamilyGroup {
  family: string;
  groupName: string;
  continuousFailedControlCount: number;
  controls: OrgFailingControlRow[];
}
function groupByFamily(
  controls: OrgFailingControlRow[],
  families: OrgControlGroupRow[],
): FamilyGroup[] {
  const repeatByFamily = new Map(
    families.map((f) => [f.family, f.continuousFailedControlCount]),
  );
  const nameByFamily = new Map(
    families.map((f) => [f.family, f.groupName?.trim() || ""]),
  );
  const buckets = new Map<string, OrgFailingControlRow[]>();
  for (const c of controls) {
    const bucket = buckets.get(c.family);
    if (bucket) {
      bucket.push(c);
    } else {
      buckets.set(c.family, [c]);
    }
  }
  return Array.from(buckets.entries())
    .map(([family, rows]) => ({
      family,
      groupName:
        nameByFamily.get(family) ||
        rows[0]?.groupName?.trim() ||
        family.toUpperCase(),
      continuousFailedControlCount:
        repeatByFamily.get(family) ??
        rows.filter((r) => r.cyclesFailed >= 2).length,
      controls: [...rows].sort((a, b) => b.cyclesFailed - a.cyclesFailed),
    }))
    .sort(
      (a, b) =>
        b.controls.length - a.controls.length ||
        a.family.localeCompare(b.family),
    );
}
function EmptyPanel({ label }: { label: string }) {
  return (
    <div className="rounded-lg border border-dashed border-[#e2e8f0] dark:border-white/[0.08] px-4 py-10 text-center">
      <ShieldCheck className="size-5 text-emerald-500 mx-auto mb-2" />
      <p className="text-xs text-[#64748b] dark:text-white/45">{label}</p>
    </div>
  );
}
function controlMeta(c: OrgFailingControlRow): string {
  const parts = [sourceLabel(c.source)];
  parts.push(
    `${c.failedApplicationCount} app${c.failedApplicationCount === 1 ? "" : "s"}`,
  );
  if (c.failedAssetCount > 0) {
    parts.push(
      `${c.failedAssetCount} asset${c.failedAssetCount === 1 ? "" : "s"}`,
    );
  }
  return parts.join(" · ");
}
function AssetTypesList({ rows }: { rows: AssetTypeFailureRow[] }) {
  const max = Math.max(1, ...rows.map((r) => r.failedAssetCount));
  return (
    <div className={CARD_CLASS}>
      <ul className="divide-y divide-[#f1f5f9] dark:divide-white/[0.04]">
        {rows.map((r) => (
          <li key={r.componentType} className="px-3 py-2">
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs font-semibold text-[#0f172a] dark:text-white truncate">
                {r.componentType}
              </span>
              <span className="text-[11px] tabular-nums text-[#475569] dark:text-white/65 flex-shrink-0">
                {r.failedAssetCount} asset
                {r.failedAssetCount === 1 ? "" : "s"} · {r.failedControlCount}{" "}
                control{r.failedControlCount === 1 ? "" : "s"}
              </span>
            </div>
            <div className="mt-1 h-1.5 rounded-full bg-[#f1f5f9] dark:bg-white/[0.06] overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${(r.failedAssetCount / max) * 100}%`,
                  background: "#0ea5e9",
                }}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}